import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { get, put, del, post } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";
import {
  DEFAULT_HEALTH_SCORE_THRESHOLDS,
  extractVendorConfigValue,
  HEALTH_SCORE_THRESHOLDS_KEY,
  normalizeHealthScoreThresholds,
  type HealthScoreThresholds,
} from "../../Utils/HealthThresholds";
import {
  normalizeFanInCap,
  normalizeFanOutCaps,
  normalizeOverallFanOutCap,
  normalizeUnknownMeddWeight,
} from "../../Utils/Helper/rlFanCaps";
import { toast } from "../../Utils/Helper/toast";
import {
  DEFAULT_LOW_SCORE_CRITERIA,
  normalizeLowScoreCriteria,
} from "../../Utils/LowScoreCriteria";

export type { HealthScoreThresholds };

export type AccountTeamRoleLimits = Record<string, number>;

export interface AccountTeamRoleLimitsConfig {
  id?: string;
  key: string;
  value: AccountTeamRoleLimits;
  created_at?: string;
  updated_at?: string;
}

export interface UpdateAccountTeamRoleLimitsPayload {
  key: string;
  value: AccountTeamRoleLimits;
}

export interface DisconnectFlowApiValue {
  reminder?: number;
  formal_reminder?: number;
  self_reminder?: number;
  default_due_date?: number;
  formal_default_due_date?: number;
  team_default_due_date?: number;
  level_gap_threshold?: string | number;
}

export interface DisconnectFlowConfig {
  id?: string;
  key: string;
  value: DisconnectFlowApiValue;
  created_at?: string;
  updated_at?: string;
}

export interface UpdateDisconnectFlowConfigPayload {
  key: string;
  value: DisconnectFlowApiValue;
}

export interface RlSettingApiValue {
  relationship_assessments?: boolean | string;
  consider_meddpicc_role?: boolean | string;
  fan_in_cap?: number;
  fan_out_caps?: Record<string, number>;
  /** Org-wide portfolio fan-out (Fan-out over-all). */
  overall_fan_out_cap?: number;
  unknown_meddpicc_default_weight?: number;
}

export interface RlSettingConfig {
  id?: string;
  key: string;
  value: RlSettingApiValue;
  created_at?: string;
  updated_at?: string;
}

export interface UpdateRlSettingConfigPayload {
  key: string;
  value: RlSettingApiValue;
}

export interface OrganizationRlSettingConfig {
  relationship_assessments?: boolean | string;
}

export interface WeeklyDigestApiValue {
  enabled?: boolean | string;
  all_sponsors_enabled?: boolean | string;
}

export interface WeeklyDigestConfig {
  id?: string;
  key: string;
  value: WeeklyDigestApiValue;
  created_at?: string;
  updated_at?: string;
}

export interface UpdateWeeklyDigestConfigPayload {
  key: string;
  value: WeeklyDigestApiValue;
}

export interface WeeklyDigestSponsorsStatus {
  all_sponsors_enabled: boolean;
  sponsor_count: number;
  enabled_count: number;
  updated_count?: number;
}

export interface SendTestWeeklyDigestPayload {
  sponsor_email: string;
  recipient_email: string;
}

export interface WeeklyDigestStatus {
  org_entitled: boolean;
  org_active: boolean;
  ui_visible: boolean;
  sending_enabled: boolean;
}

export interface OrganizationSettingsContent {
  rl_setting_config?: OrganizationRlSettingConfig;
  weekly_digest?: { enabled?: boolean | string };
  product_telemetrics?: { enabled?: boolean | string };
  coverage_analysis?: { enabled?: boolean | string };
  [key: string]: unknown;
}

export type LowScoreCriteriaConfigValue = {
  renewal_enabled: boolean;
  renewal_days: number;
  rl_gap_enabled: boolean;
  rl_gap_levels: number;
  assessment_stale_enabled: boolean;
  assessment_stale_days: number;
  signal_recency_enabled: boolean;
  signal_recency_days: number;
};

export type PortfolioNeedsAttentionSettings = {
  health_threshold: number;
  renewal_days: number;
  include_triage: boolean;
  include_defend: boolean;
  include_bh_concern: boolean;
  include_unscored: boolean;
  include_unmapped: boolean;
  include_unknown_al: boolean;
};

export interface UpdateLowScoreCriteriaPayload {
  key: string;
  value: LowScoreCriteriaConfigValue;
}

export interface UpdatePortfolioNeedsAttentionPayload {
  key: string;
  value: PortfolioNeedsAttentionSettings;
}

const DEFAULT_PORTFOLIO_NEEDS_ATTENTION: PortfolioNeedsAttentionSettings = {
  health_threshold: 65,
  renewal_days: 90,
  include_triage: false,
  include_defend: false,
  include_bh_concern: true,
  include_unscored: false,
  include_unmapped: false,
  include_unknown_al: false,
};

const normalizePortfolioNeedsAttentionValue = (
  raw?: Partial<PortfolioNeedsAttentionSettings> | null
): PortfolioNeedsAttentionSettings => {
  const health = Number(raw?.health_threshold);
  const renewal = Number(raw?.renewal_days);
  return {
    health_threshold: Number.isFinite(health)
      ? Math.max(0, Math.min(100, Math.round(health)))
      : DEFAULT_PORTFOLIO_NEEDS_ATTENTION.health_threshold,
    renewal_days: Number.isFinite(renewal)
      ? Math.max(0, Math.round(renewal))
      : DEFAULT_PORTFOLIO_NEEDS_ATTENTION.renewal_days,
    include_triage: Boolean(raw?.include_triage),
    include_defend: Boolean(raw?.include_defend),
    include_bh_concern:
      raw?.include_bh_concern ??
      DEFAULT_PORTFOLIO_NEEDS_ATTENTION.include_bh_concern,
    include_unscored: Boolean(raw?.include_unscored),
    include_unmapped: Boolean(raw?.include_unmapped),
    include_unknown_al: Boolean(raw?.include_unknown_al),
  };
};

const extractConfigValue = <T extends object>(
  payload: unknown
): Partial<T> | null | undefined => {
  if (!payload || typeof payload !== "object") return undefined;
  const record = payload as Record<string, unknown>;
  if (record.value && typeof record.value === "object") {
    return record.value as Partial<T>;
  }
  return record as Partial<T>;
};

const normalizeAccountTeamRoleLimits = (
  value?: Record<string, unknown> | null
): AccountTeamRoleLimits => {
  if (!value || typeof value !== "object") return {};
  return Object.fromEntries(
    Object.entries(value).map(([key, raw]) => [key, Number(raw) || 0])
  );
};

const parseApiErrorMessage = (error: unknown, fallback: string): string => {
  const axiosData = (error as { response?: { data?: any } })?.response?.data;
  if (typeof axiosData?.response?.message === "string") {
    return axiosData.response.message;
  }
  if (typeof axiosData?.message === "string") {
    return axiosData.message;
  }
  if (typeof axiosData?.detail === "string") {
    return axiosData.detail;
  }
  try {
    const { message } = JSON.parse((error as Error).message) as {
      message?: string;
    };
    return message || fallback;
  } catch {
    return fallback;
  }
};

const normalizeBoolConfigValue = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }
  if (typeof value === "number") return value === 1;
  return false;
};

const normalizeRlSettingValue = (
  value?: Record<string, unknown> | null
): Required<RlSettingApiValue> => {
  return {
    relationship_assessments: normalizeBoolConfigValue(
      value?.relationship_assessments
    ),
    consider_meddpicc_role: normalizeBoolConfigValue(
      value?.consider_meddpicc_role
    ),
    fan_in_cap: normalizeFanInCap(value?.fan_in_cap),
    fan_out_caps: normalizeFanOutCaps(
      (value?.fan_out_caps as Record<string, unknown> | undefined) || null
    ),
    overall_fan_out_cap: normalizeOverallFanOutCap(value?.overall_fan_out_cap),
    unknown_meddpicc_default_weight: normalizeUnknownMeddWeight(
      value?.unknown_meddpicc_default_weight
    ),
  };
};

const normalizeWeeklyDigestValue = (
  value?: Record<string, unknown> | null
): Required<WeeklyDigestApiValue> => ({
  enabled:
    value?.enabled === undefined
      ? true
      : normalizeBoolConfigValue(value?.enabled),
  all_sponsors_enabled:
    value?.all_sponsors_enabled === undefined
      ? false
      : normalizeBoolConfigValue(value?.all_sponsors_enabled),
});

interface User {
  user_id: string;
  name: string;
  email: string;
  role_name: string;
  status: string;
  created_at: string;
}

interface VendorAdminState {
  statistics: any | null;
  grades: any | null;
  accountTiers: any | null;
  accountTiersLoading: boolean;
  colorCodingThresholds: any | null;
  colorCodingThresholdId: string | null;
  assignmentRecommendationSettings: any | null;
  assignmentRecommendationHistory: any[];
  usersResponse: {
    total: number;
    users: User[];
    current_page: number;
    page_size: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  } | null;
  loading: boolean;
  error: string | null;
  updateLoading: boolean;
  updateError: string | null;
  updateSuccess: boolean;
  colorCodingLoading: boolean;
  colorCodingError: string | null;
  colorCodingUpdateLoading: boolean;
  colorCodingUpdateError: string | null;
  colorCodingUpdateSuccess: boolean;
  assignmentRecommendationSettingsLoading: boolean;
  assignmentRecommendationSettingsError: string | null;
  assignmentRecommendationUpdateLoading: boolean;
  assignmentRecommendationUpdateError: string | null;
  assignmentRecommendationUpdateSuccess: boolean;
  assignmentRecommendationHistoryLoading: boolean;
  assignmentRecommendationHistoryError: string | null;
  deleteUserLoading: boolean;
  deleteUserError: string | null;
  deleteUserSuccess: boolean;
  resetPasswordLoading: boolean;
  resetPasswordError: string | null;
  resetPasswordSuccess: boolean;
  updateUserLoading: boolean;
  updateUserError: string | null;
  updateUserSuccess: boolean;
  createUserLoading: boolean;
  createUserError: string | null;
  createUserSuccess: boolean;
  resendWelcomeEmailLoading: boolean;
  resendWelcomeEmailError: string | null;
  resendWelcomeEmailSuccess: boolean;
  accountTeamRoleLimits: AccountTeamRoleLimits;
  accountTeamRoleLimitsConfigId: string | null;
  accountTeamRoleLimitsConfigKey: string | null;
  accountTeamRoleLimitsLoading: boolean;
  accountTeamRoleLimitsError: string | null;
  accountTeamRoleLimitsUpdateLoading: boolean;
  accountTeamRoleLimitsUpdateError: string | null;
  disconnectFlowValue: DisconnectFlowApiValue | null;
  disconnectFlowConfigId: string | null;
  disconnectFlowConfigKey: string | null;
  disconnectFlowSettingsLoading: boolean;
  disconnectFlowSettingsError: string | null;
  disconnectFlowSettingsUpdateLoading: boolean;
  disconnectFlowSettingsUpdateError: string | null;
  rlSettingValue: Required<RlSettingApiValue> | null;
  rlSettingConfigId: string | null;
  rlSettingConfigKey: string | null;
  rlSettingLoading: boolean;
  rlSettingError: string | null;
  rlSettingUpdateLoading: boolean;
  rlSettingUpdateError: string | null;
  weeklyDigestValue: Required<WeeklyDigestApiValue> | null;
  weeklyDigestLoading: boolean;
  weeklyDigestError: string | null;
  weeklyDigestUpdateLoading: boolean;
  weeklyDigestUpdateError: string | null;
  weeklyDigestStatus: WeeklyDigestStatus | null;
  weeklyDigestStatusLoading: boolean;
  organizationSettings: OrganizationSettingsContent | null;
  organizationSettingsLoading: boolean;
  organizationSettingsFetched: boolean;
  organizationSettingsError: string | null;
  portfolioMapsConfig: unknown | null;
  portfolioMapsConfigLoading: boolean;
  portfolioMapsConfigFetched: boolean;
  portfolioMapsConfigError: string | null;
  healthScoreThresholds: HealthScoreThresholds;
  healthScoreThresholdsLoading: boolean;
  healthScoreThresholdsFetched: boolean;
  healthScoreThresholdsError: string | null;
  healthScoreThresholdsUpdateLoading: boolean;
  healthScoreThresholdsUpdateError: string | null;
  healthScoreThresholdsUpdateSuccess: boolean;
  lowScoreCriteriaValue: LowScoreCriteriaConfigValue | null;
  lowScoreCriteriaLoading: boolean;
  lowScoreCriteriaError: string | null;
  lowScoreCriteriaUpdateLoading: boolean;
  lowScoreCriteriaUpdateError: string | null;
  portfolioNeedsAttentionValue: PortfolioNeedsAttentionSettings | null;
  portfolioNeedsAttentionLoading: boolean;
  portfolioNeedsAttentionError: string | null;
  portfolioNeedsAttentionUpdateLoading: boolean;
  portfolioNeedsAttentionUpdateError: string | null;
}

const initialState: VendorAdminState = {
  statistics: null,
  grades: null,
  accountTiers: null,
  accountTiersLoading: false,
  colorCodingThresholds: null,
  colorCodingThresholdId: null,
  assignmentRecommendationSettings: null,
  assignmentRecommendationHistory: [],
  usersResponse: null,
  loading: false,
  error: null,
  updateLoading: false,
  updateError: null,
  updateSuccess: false,
  colorCodingLoading: false,
  colorCodingError: null,
  colorCodingUpdateLoading: false,
  colorCodingUpdateError: null,
  colorCodingUpdateSuccess: false,
  assignmentRecommendationSettingsLoading: false,
  assignmentRecommendationSettingsError: null,
  assignmentRecommendationUpdateLoading: false,
  assignmentRecommendationUpdateError: null,
  assignmentRecommendationUpdateSuccess: false,
  assignmentRecommendationHistoryLoading: false,
  assignmentRecommendationHistoryError: null,
  deleteUserLoading: false,
  deleteUserError: null,
  deleteUserSuccess: false,
  resetPasswordLoading: false,
  resetPasswordError: null,
  resetPasswordSuccess: false,
  updateUserLoading: false,
  updateUserError: null,
  updateUserSuccess: false,
  createUserLoading: false,
  createUserError: null,
  createUserSuccess: false,
  resendWelcomeEmailLoading: false,
  resendWelcomeEmailError: null,
  resendWelcomeEmailSuccess: false,
  accountTeamRoleLimits: {},
  accountTeamRoleLimitsConfigId: null,
  accountTeamRoleLimitsConfigKey: null,
  accountTeamRoleLimitsLoading: false,
  accountTeamRoleLimitsError: null,
  accountTeamRoleLimitsUpdateLoading: false,
  accountTeamRoleLimitsUpdateError: null,
  disconnectFlowValue: null,
  disconnectFlowConfigId: null,
  disconnectFlowConfigKey: null,
  disconnectFlowSettingsLoading: false,
  disconnectFlowSettingsError: null,
  disconnectFlowSettingsUpdateLoading: false,
  disconnectFlowSettingsUpdateError: null,
  rlSettingValue: null,
  rlSettingConfigId: null,
  rlSettingConfigKey: null,
  rlSettingLoading: false,
  rlSettingError: null,
  rlSettingUpdateLoading: false,
  rlSettingUpdateError: null,
  weeklyDigestValue: null,
  weeklyDigestLoading: false,
  weeklyDigestError: null,
  weeklyDigestUpdateLoading: false,
  weeklyDigestUpdateError: null,
  weeklyDigestStatus: null,
  weeklyDigestStatusLoading: false,
  organizationSettings: null,
  organizationSettingsLoading: false,
  organizationSettingsFetched: false,
  organizationSettingsError: null,
  portfolioMapsConfig: null,
  portfolioMapsConfigLoading: false,
  portfolioMapsConfigFetched: false,
  portfolioMapsConfigError: null,
  healthScoreThresholds: DEFAULT_HEALTH_SCORE_THRESHOLDS,
  healthScoreThresholdsLoading: false,
  healthScoreThresholdsFetched: false,
  healthScoreThresholdsError: null,
  healthScoreThresholdsUpdateLoading: false,
  healthScoreThresholdsUpdateError: null,
  healthScoreThresholdsUpdateSuccess: false,
  lowScoreCriteriaValue: null,
  lowScoreCriteriaLoading: false,
  lowScoreCriteriaError: null,
  lowScoreCriteriaUpdateLoading: false,
  lowScoreCriteriaUpdateError: null,
  portfolioNeedsAttentionValue: null,
  portfolioNeedsAttentionLoading: false,
  portfolioNeedsAttentionError: null,
  portfolioNeedsAttentionUpdateLoading: false,
  portfolioNeedsAttentionUpdateError: null,
};

export interface UpdateGradePayload {
  grade_id: string;
  grade_name: string;
  capacity_limits: number;
}

export interface UpdateAccountTierPayload {
  tier_id: string;
  tier_name: string;
}

export interface ResetPasswordPayload {
  user_id: string;
}

export interface CreateUserPayload {
  email: string;
  name: string;
  role_name: string;
  position?: string;
  phone?: string;
  city?: string;
  country?: string;
  join_date?: string;
  send_invitation?: boolean;
}

export interface UpdateUserPayload {
  user_id: string;
  name?: string;
  email?: string;
  role_name?: string;
  status?: string;
  phone?: string;
  position?: string;
  secondary_role?: string;
  executive_ops?: boolean;
  executive_ops_approve?: boolean;
  weekly_digest_email?: boolean;
}

export interface UpdateColorCodingThresholdPayload {
  green_zone_upper_limit: number;
  yellow_zone_upper_limit: number;
}

export const fetchVendorAdminStatistics = createAsyncThunk(
  "vendorAdmin/fetchStatistics",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.fetchVendorAdminStatistics());
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch statistics"
      );
    }
  }
);

export const fetchVendorAdminGrades = createAsyncThunk(
  "vendorAdmin/fetchGrades",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.fetchVendorAdminGrades());
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch grades"
      );
    }
  }
);

export const fetchVendorAdminAccountTiers = createAsyncThunk(
  "vendorAdmin/fetchAccountTiers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.fetchVendorAdminAccountTiers());
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch account tiers"
      );
    }
  }
);

export const fetchVendorAdminUsers = createAsyncThunk(
  "vendorAdmin/fetchUsers",
  async (
    params: { page?: number; page_size?: number; search?: string } | undefined,
    { rejectWithValue }: any
  ) => {
    try {
      const response = await get(API_URLS.fetchVendorAdminUsers(params));
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch users"
      );
    }
  }
);

export const updateGrade = createAsyncThunk(
  "vendorAdmin/updateGrade",
  async (payload: UpdateGradePayload, { rejectWithValue }) => {
    try {
      const { grade_id, ...data } = payload;
      const response = await put(
        API_URLS.updateVendorAdminGrade(grade_id),
        data
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update grade"
      );
    }
  }
);

export const updateAccountTier = createAsyncThunk(
  "vendorAdmin/updateAccountTier",
  async (payload: UpdateAccountTierPayload, { rejectWithValue }) => {
    try {
      const { tier_id, ...data } = payload;
      const response = await put(
        API_URLS.updateVendorAdminAccountTier(tier_id),
        data
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update account tier"
      );
    }
  }
);

export const deleteVendorUser = createAsyncThunk(
  "vendorAdmin/deleteUser",
  async (userId: string, { rejectWithValue }) => {
    try {
      await del(API_URLS.deleteVendorUser(userId));
      return userId;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete user"
      );
    }
  }
);

export const resetVendorUserPassword = createAsyncThunk(
  "vendorAdmin/resetPassword",
  async (payload: ResetPasswordPayload, { rejectWithValue }) => {
    try {
      await post(API_URLS.resetVendorUserPassword(), payload);
      return payload.user_id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reset password"
      );
    }
  }
);

export const createVendorUser = createAsyncThunk(
  "vendorAdmin/createUser",
  async (payload: CreateUserPayload, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.createVendorAdminUser(), payload);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(
        parseApiErrorMessage(error, "Failed to create user")
      );
    }
  }
);

export const updateVendorUser = createAsyncThunk(
  "vendorAdmin/updateUser",
  async (payload: UpdateUserPayload, { rejectWithValue }) => {
    try {
      const { user_id, ...data } = payload;
      const response = await put(API_URLS.updateVendorUser(user_id), data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(
        parseApiErrorMessage(error, "Failed to update user")
      );
    }
  }
);

export const resendWelcomeEmail = createAsyncThunk(
  "vendorAdmin/resendWelcomeEmail",
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.resendWelcomeEmail(userId));
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send invitation email"
      );
    }
  }
);

export const fetchColorCodingThresholds = createAsyncThunk(
  "vendorAdmin/fetchColorCodingThresholds",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.fetchColorCodingThresholds());
      // Handle both direct response and wrapped response
      const data = response?.payload || response;

      // Transform API response to component's expected structure
      if (data && data.green_zone_upper_limit !== undefined) {
        return {
          id: data.id,
          utilization_percentage: {
            greenThreshold: data.green_zone_upper_limit,
            yellowThreshold: data.yellow_zone_upper_limit,
          },
        };
      }

      // If already in expected format, return as is
      return data;
    } catch (error: any) {
      // Return defaults if API doesn't exist yet
      const defaults = {
        id: null,
        utilization_percentage: {
          greenThreshold: 100.0,
          yellowThreshold: 120.0,
        },
      };
      // Only reject if it's not a 404 or similar "not found" error
      if (error?.response?.status && error.response.status !== 404) {
        return rejectWithValue(
          error.response?.data?.message ||
            error?.message ||
            "Failed to fetch color coding thresholds"
        );
      }
      return defaults;
    }
  }
);

export const updateColorCodingThresholds = createAsyncThunk(
  "vendorAdmin/updateColorCodingThresholds",
  async (
    payload: { thresholdId: string; data: UpdateColorCodingThresholdPayload },
    { rejectWithValue }
  ) => {
    try {
      const response = await put(
        API_URLS.updateColorCodingThresholds(payload.thresholdId),
        payload.data
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error?.message ||
          "Failed to update color coding thresholds"
      );
    }
  }
);

export const fetchAssignmentRecommendationSettings = createAsyncThunk(
  "vendorAdmin/fetchAssignmentRecommendationSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(
        API_URLS.fetchAssignmentRecommendationSettings()
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error?.message ||
          "Failed to fetch assignment recommendation settings"
      );
    }
  }
);

export const updateAssignmentRecommendationSettings = createAsyncThunk(
  "vendorAdmin/updateAssignmentRecommendationSettings",
  async (
    payload: { settings: Record<string, any>; changeReason: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await put(
        API_URLS.updateAssignmentRecommendationSettings(),
        {
          ...payload.settings,
          change_reason: payload.changeReason,
        }
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error?.message ||
          "Failed to update assignment recommendation settings"
      );
    }
  }
);

export const fetchAssignmentRecommendationHistory = createAsyncThunk(
  "vendorAdmin/fetchAssignmentRecommendationHistory",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(
        API_URLS.fetchAssignmentRecommendationHistory()
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error?.message ||
          "Failed to fetch assignment recommendation history"
      );
    }
  }
);

export const fetchAccountTeamRoleLimits = createAsyncThunk<
  AccountTeamRoleLimitsConfig,
  void,
  { rejectValue: string }
>("vendorAdmin/fetchAccountTeamRoleLimits", async (_, { rejectWithValue }) => {
  try {
    const response = (await get(API_URLS.fetchAccountTeamRoleLimits())) as {
      status?: string;
      message?: string;
      content?: AccountTeamRoleLimitsConfig;
    };

    if (response?.status === "failure" || response?.status === "unauthorized") {
      return rejectWithValue(
        response.message || "Failed to fetch account team role limits"
      );
    }

    const payload = (response?.content ??
      response) as AccountTeamRoleLimitsConfig;
    if (!payload?.key) {
      return rejectWithValue("Account team role limits config key is missing");
    }
    return {
      id: payload.id,
      key: payload.key,
      value: normalizeAccountTeamRoleLimits(payload.value),
      created_at: payload.created_at,
      updated_at: payload.updated_at,
    };
  } catch (error: any) {
    return rejectWithValue(
      parseApiErrorMessage(error, "Failed to fetch account team role limits")
    );
  }
});

export const fetchDisconnectFlowConfig = createAsyncThunk<
  DisconnectFlowConfig,
  void,
  { rejectValue: string }
>("vendorAdmin/fetchDisconnectFlowConfig", async (_, { rejectWithValue }) => {
  try {
    const response = (await get(API_URLS.fetchDisconnectFlowConfig())) as {
      status?: string;
      message?: string;
      content?: DisconnectFlowConfig & { value?: DisconnectFlowApiValue };
    } & DisconnectFlowConfig & { value?: DisconnectFlowApiValue };

    if (response?.status === "failure" || response?.status === "unauthorized") {
      return rejectWithValue(
        response.message || "Failed to fetch disconnect workflow settings"
      );
    }

    const payload = (response?.content ?? response) as DisconnectFlowConfig & {
      value?: DisconnectFlowApiValue;
    };
    if (!payload?.key && !payload?.value) {
      return rejectWithValue("Disconnect workflow settings are missing");
    }

    return {
      id: payload.id,
      key: payload.key,
      value: payload.value ?? {},
      created_at: payload.created_at,
      updated_at: payload.updated_at,
    };
  } catch (error: any) {
    return rejectWithValue(
      parseApiErrorMessage(
        error,
        "Failed to fetch disconnect workflow settings"
      )
    );
  }
});

export const updateDisconnectFlowConfig = createAsyncThunk<
  DisconnectFlowConfig,
  UpdateDisconnectFlowConfigPayload,
  { rejectValue: string }
>(
  "vendorAdmin/updateDisconnectFlowConfig",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const response = (await post(API_URLS.upsertDisconnectFlowConfig(), {
        key,
        value,
      })) as {
        status?: string;
        message?: string;
        content?: DisconnectFlowConfig & { value?: DisconnectFlowApiValue };
      };

      if (
        response?.status === "failure" ||
        response?.status === "unauthorized"
      ) {
        return rejectWithValue(
          response.message || "Failed to save disconnect workflow settings"
        );
      }

      const payload = (response?.content ??
        response) as DisconnectFlowConfig & {
        value?: DisconnectFlowApiValue;
      };
      return {
        id: payload?.id,
        key: payload?.key || key,
        value: payload?.value ?? value,
        created_at: payload?.created_at,
        updated_at: payload?.updated_at,
      };
    } catch (error: any) {
      return rejectWithValue(
        parseApiErrorMessage(
          error,
          "Failed to save disconnect workflow settings"
        )
      );
    }
  }
);

export const isRlAssessmentsEnabledFromSettings = (
  settings: OrganizationSettingsContent | null | undefined
): boolean =>
  normalizeBoolConfigValue(
    settings?.rl_setting_config?.relationship_assessments
  );

export const isProductTelemetricsEnabledFromSettings = (
  settings: OrganizationSettingsContent | null | undefined
): boolean => normalizeBoolConfigValue(settings?.product_telemetrics?.enabled);

export const isCoverageAnalysisEnabledFromSettings = (
  settings: OrganizationSettingsContent | null | undefined
): boolean => normalizeBoolConfigValue(settings?.coverage_analysis?.enabled);

export const selectOrganizationSettingsAccess = (state: {
  vendorAdmin: VendorAdminState;
}) => {
  const {
    organizationSettings,
    organizationSettingsFetched,
    organizationSettingsError,
  } = state.vendorAdmin;
  const resolved = organizationSettingsFetched;
  const loading = !resolved;
  const enabled = resolved
    ? isRlAssessmentsEnabledFromSettings(organizationSettings)
    : false;

  return {
    resolved,
    loading,
    enabled,
    error: organizationSettingsError,
  };
};

export const selectProductTelemetricsAccess = (state: {
  vendorAdmin: VendorAdminState;
}) => {
  const {
    organizationSettings,
    organizationSettingsFetched,
    organizationSettingsError,
  } = state.vendorAdmin;
  const resolved = organizationSettingsFetched;
  const enabled = resolved
    ? isProductTelemetricsEnabledFromSettings(organizationSettings)
    : false;

  return {
    resolved,
    enabled,
    error: organizationSettingsError,
  };
};

export const selectCoverageAnalysisAccess = (state: {
  vendorAdmin: VendorAdminState;
}) => {
  const {
    organizationSettings,
    organizationSettingsFetched,
    organizationSettingsError,
  } = state.vendorAdmin;
  const resolved = organizationSettingsFetched;
  const enabled = resolved
    ? isCoverageAnalysisEnabledFromSettings(organizationSettings)
    : false;

  return {
    resolved,
    enabled,
    error: organizationSettingsError,
  };
};

export const selectPortfolioMapsConfig = (state: {
  vendorAdmin: VendorAdminState;
}) => {
  const {
    portfolioMapsConfig,
    portfolioMapsConfigFetched,
    portfolioMapsConfigError,
  } = state.vendorAdmin;
  return {
    resolved: portfolioMapsConfigFetched,
    raw: portfolioMapsConfig,
    error: portfolioMapsConfigError,
  };
};

export const fetchPortfolioMapsConfig = createAsyncThunk<
  unknown,
  void,
  { rejectValue: string }
>(
  "vendorAdmin/fetchPortfolioMapsConfig",
  async (_, { rejectWithValue }) => {
    try {
      const response = (await get(API_URLS.fetchPortfolioMapsConfig())) as {
        content?: unknown;
      };
      const content = response?.content;
      if (!content || typeof content !== "object") {
        return rejectWithValue("Portfolio Maps config is missing");
      }
      return content;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.detail ||
          "Failed to fetch Portfolio Maps config"
      );
    }
  },
  {
    condition: (_, { getState }) => {
      const { portfolioMapsConfigLoading } = (
        getState() as { vendorAdmin: VendorAdminState }
      ).vendorAdmin;
      return !portfolioMapsConfigLoading;
    },
  }
);

export const fetchOrganizationSettings = createAsyncThunk<
  OrganizationSettingsContent,
  void,
  { rejectValue: string }
>(
  "vendorAdmin/fetchOrganizationSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = (await get(API_URLS.fetchOrganizationSettings())) as {
        content?: { settings?: OrganizationSettingsContent };
        response?: { status?: number; message?: string };
      };

      const settings = response?.content?.settings;
      if (!settings || typeof settings !== "object") {
        return rejectWithValue("Organization settings are missing");
      }

      return settings;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          error?.message ||
          "Failed to fetch organization settings"
      );
    }
  },
  {
    condition: (_, { getState }) => {
      const { organizationSettingsLoading } = (
        getState() as { vendorAdmin: VendorAdminState }
      ).vendorAdmin;
      return !organizationSettingsLoading;
    },
  }
);

export const fetchRlSettingConfig = createAsyncThunk<
  RlSettingConfig,
  void,
  { rejectValue: string }
>("vendorAdmin/fetchRlSettingConfig", async (_, { rejectWithValue }) => {
  try {
    const response = (await get(API_URLS.fetchRlSettingConfig())) as {
      status?: string;
      message?: string;
      content?: RlSettingConfig;
    } & RlSettingConfig;

    if (response?.status === "failure" || response?.status === "unauthorized") {
      return rejectWithValue(response.message || "Failed to fetch RL settings");
    }

    const payload = (response?.content ?? response) as RlSettingConfig;
    if (!payload?.key) {
      return rejectWithValue("RL settings config key is missing");
    }

    return {
      id: payload.id,
      key: payload.key,
      value: normalizeRlSettingValue(payload.value as Record<string, unknown>),
      created_at: payload.created_at,
      updated_at: payload.updated_at,
    };
  } catch (error: any) {
    return rejectWithValue(
      parseApiErrorMessage(error, "Failed to fetch RL settings")
    );
  }
});

export const fetchHealthScoreThresholds = createAsyncThunk<
  HealthScoreThresholds,
  void,
  { rejectValue: string }
>(
  "vendorAdmin/fetchHealthScoreThresholds",
  async (_, { rejectWithValue }) => {
    try {
      const response = (await get(
        API_URLS.fetchHealthScoreThresholdsConfig()
      )) as {
        status?: string;
        message?: string;
        content?: unknown;
      };

      if (
        response?.status === "failure" ||
        response?.status === "unauthorized"
      ) {
        return rejectWithValue(
          response.message || "Failed to fetch health score thresholds"
        );
      }

      return normalizeHealthScoreThresholds(
        extractVendorConfigValue<HealthScoreThresholds>(
          response?.content ?? response
        )
      );
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          error?.message ||
          "Failed to fetch health score thresholds"
      );
    }
  },
  {
    condition: (_, { getState }) => {
      const { healthScoreThresholdsLoading, healthScoreThresholdsFetched } = (
        getState() as { vendorAdmin: VendorAdminState }
      ).vendorAdmin;
      return !healthScoreThresholdsLoading && !healthScoreThresholdsFetched;
    },
  }
);

export const updateHealthScoreThresholds = createAsyncThunk<
  HealthScoreThresholds,
  HealthScoreThresholds,
  { rejectValue: string }
>(
  "vendorAdmin/updateHealthScoreThresholds",
  async (value, { rejectWithValue }) => {
    try {
      const normalized = normalizeHealthScoreThresholds(value);
      const response = (await post(API_URLS.upsertVendorConfig(), {
        key: HEALTH_SCORE_THRESHOLDS_KEY,
        value: normalized,
      })) as {
        status?: string;
        message?: string;
        content?: unknown;
      };

      if (
        response?.status === "failure" ||
        response?.status === "unauthorized"
      ) {
        return rejectWithValue(
          response.message || "Failed to save health score thresholds"
        );
      }

      return normalizeHealthScoreThresholds(
        extractVendorConfigValue<HealthScoreThresholds>(
          response?.content ?? response
        ) ?? normalized
      );
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          error?.message ||
          "Failed to save health score thresholds"
      );
    }
  }
);

export const updateRlSettingConfig = createAsyncThunk<
  RlSettingConfig,
  UpdateRlSettingConfigPayload,
  { rejectValue: string }
>(
  "vendorAdmin/updateRlSettingConfig",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const normalizedValue = normalizeRlSettingValue(
        value as Record<string, unknown>
      );
      const payloadValue: Record<string, unknown> = {};
      if (value.relationship_assessments !== undefined) {
        payloadValue.relationship_assessments =
          normalizedValue.relationship_assessments ? "true" : "false";
      }
      if (value.consider_meddpicc_role !== undefined) {
        payloadValue.consider_meddpicc_role =
          normalizedValue.consider_meddpicc_role ? "true" : "false";
      }
      if (value.fan_in_cap !== undefined) {
        payloadValue.fan_in_cap = normalizedValue.fan_in_cap;
      }
      if (value.fan_out_caps !== undefined) {
        payloadValue.fan_out_caps = normalizedValue.fan_out_caps;
      }
      if (value.overall_fan_out_cap !== undefined) {
        payloadValue.overall_fan_out_cap = normalizedValue.overall_fan_out_cap;
      }
      const response = (await post(API_URLS.upsertVendorConfig(), {
        key,
        value: payloadValue,
      })) as {
        status?: string;
        message?: string;
        content?: RlSettingConfig;
      };

      if (
        response?.status === "failure" ||
        response?.status === "unauthorized"
      ) {
        return rejectWithValue(
          response.message || "Failed to save RL settings"
        );
      }

      const payload = (response?.content ?? response) as RlSettingConfig;
      return {
        id: payload?.id,
        key: payload?.key || key,
        value: normalizeRlSettingValue(
          payload?.value as Record<string, unknown>
        ),
        created_at: payload?.created_at,
        updated_at: payload?.updated_at,
      };
    } catch (error: any) {
      return rejectWithValue(
        parseApiErrorMessage(error, "Failed to save RL settings")
      );
    }
  }
);

export const fetchWeeklyDigestConfig = createAsyncThunk<
  WeeklyDigestConfig,
  void,
  { rejectValue: string }
>("vendorAdmin/fetchWeeklyDigestConfig", async (_, { rejectWithValue }) => {
  try {
    const response = (await get(API_URLS.fetchWeeklyDigestConfig())) as {
      status?: string;
      message?: string;
      content?: WeeklyDigestConfig;
    } & WeeklyDigestConfig;

    if (response?.status === "failure" || response?.status === "unauthorized") {
      return rejectWithValue(
        response.message || "Failed to fetch weekly digest settings"
      );
    }

    const payload = (response?.content ?? response) as WeeklyDigestConfig;
    return {
      id: payload?.id,
      key: payload?.key || "weekly_digest",
      value: normalizeWeeklyDigestValue(
        payload?.value as Record<string, unknown>
      ),
      created_at: payload?.created_at,
      updated_at: payload?.updated_at,
    };
  } catch (error: any) {
    return rejectWithValue(
      parseApiErrorMessage(error, "Failed to fetch weekly digest settings")
    );
  }
});

export const fetchWeeklyDigestStatus = createAsyncThunk<
  WeeklyDigestStatus,
  void,
  { rejectValue: string }
>("vendorAdmin/fetchWeeklyDigestStatus", async (_, { rejectWithValue }) => {
  try {
    const response = (await get(API_URLS.fetchWeeklyDigestStatus())) as
      | WeeklyDigestStatus
      | {
          status?: string;
          message?: string;
          content?: WeeklyDigestStatus;
        };

    if (
      response &&
      typeof response === "object" &&
      "status" in response &&
      (response.status === "failure" || response.status === "unauthorized")
    ) {
      return rejectWithValue(
        response.message || "Failed to fetch weekly digest status"
      );
    }

    const payload =
      response &&
      typeof response === "object" &&
      "content" in response &&
      response.content
        ? response.content
        : (response as WeeklyDigestStatus);

    return {
      org_entitled: Boolean(payload.org_entitled),
      org_active: Boolean(payload.org_active),
      ui_visible: Boolean(payload.ui_visible),
      sending_enabled: Boolean(payload.sending_enabled),
    };
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.detail ||
        error.response?.data?.message ||
        error?.message ||
        "Failed to fetch weekly digest status"
    );
  }
});

export const updateWeeklyDigestConfig = createAsyncThunk<
  WeeklyDigestConfig,
  UpdateWeeklyDigestConfigPayload,
  { rejectValue: string }
>(
  "vendorAdmin/updateWeeklyDigestConfig",
  async ({ key, value }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { vendorAdmin?: VendorAdminState };
      const current = state.vendorAdmin?.weeklyDigestValue;
      const normalizedValue = normalizeWeeklyDigestValue({
        enabled: value?.enabled ?? current?.enabled,
        all_sponsors_enabled:
          value?.all_sponsors_enabled ?? current?.all_sponsors_enabled,
      } as Record<string, unknown>);
      const response = (await post(API_URLS.upsertVendorConfig(), {
        key,
        value: {
          enabled: normalizedValue.enabled ? "true" : "false",
          all_sponsors_enabled: normalizedValue.all_sponsors_enabled
            ? "true"
            : "false",
        },
      })) as {
        status?: string;
        message?: string;
        content?: WeeklyDigestConfig;
      };

      if (
        response?.status === "failure" ||
        response?.status === "unauthorized"
      ) {
        return rejectWithValue(
          response.message || "Failed to save weekly digest settings"
        );
      }

      const payload = (response?.content ?? response) as WeeklyDigestConfig;
      return {
        id: payload?.id,
        key: payload?.key || key,
        value: normalizeWeeklyDigestValue(
          payload?.value as Record<string, unknown>
        ),
        created_at: payload?.created_at,
        updated_at: payload?.updated_at,
      };
    } catch (error: any) {
      return rejectWithValue(
        parseApiErrorMessage(error, "Failed to save weekly digest settings")
      );
    }
  }
);

export const fetchWeeklyDigestSponsors = createAsyncThunk<
  WeeklyDigestSponsorsStatus,
  void,
  { rejectValue: string }
>("vendorAdmin/fetchWeeklyDigestSponsors", async (_, { rejectWithValue }) => {
  try {
    const response = (await get(
      API_URLS.fetchWeeklyDigestSponsors()
    )) as WeeklyDigestSponsorsStatus & {
      content?: WeeklyDigestSponsorsStatus;
      status?: string;
      message?: string;
    };

    if (response?.status === "failure" || response?.status === "unauthorized") {
      return rejectWithValue(
        response.message || "Failed to load sponsor digest settings."
      );
    }

    return (response?.content ?? response) as WeeklyDigestSponsorsStatus;
  } catch (error: any) {
    return rejectWithValue(
      parseApiErrorMessage(error, "Failed to load sponsor digest settings.")
    );
  }
});

export const updateWeeklyDigestSponsors = createAsyncThunk<
  WeeklyDigestSponsorsStatus,
  { enabled: boolean },
  { rejectValue: string }
>(
  "vendorAdmin/updateWeeklyDigestSponsors",
  async ({ enabled }, { rejectWithValue }) => {
    try {
      const response = (await put(API_URLS.updateWeeklyDigestSponsors(), {
        enabled,
      })) as WeeklyDigestSponsorsStatus & {
        content?: WeeklyDigestSponsorsStatus;
        status?: string;
        message?: string;
      };

      if (
        response?.status === "failure" ||
        response?.status === "unauthorized"
      ) {
        return rejectWithValue(
          response.message || "Failed to update sponsor digest settings."
        );
      }

      return (response?.content ?? response) as WeeklyDigestSponsorsStatus;
    } catch (error: any) {
      return rejectWithValue(
        parseApiErrorMessage(error, "Failed to update sponsor digest settings.")
      );
    }
  }
);

export const sendTestWeeklyDigest = createAsyncThunk<
  void,
  SendTestWeeklyDigestPayload,
  { rejectValue: string }
>("vendorAdmin/sendTestWeeklyDigest", async (payload, { rejectWithValue }) => {
  try {
    await post(API_URLS.sendTestWeeklyDigest(), payload);
  } catch (error: any) {
    return rejectWithValue(
      parseApiErrorMessage(error, "Failed to send the weekly digest.")
    );
  }
});

export const fetchLowScoreCriteriaConfig = createAsyncThunk<
  LowScoreCriteriaConfigValue,
  void,
  { rejectValue: string }
>("vendorAdmin/fetchLowScoreCriteriaConfig", async (_, { rejectWithValue }) => {
  try {
    const response = (await get(API_URLS.fetchLowScoreCriteriaConfig())) as {
      status?: string;
      message?: string;
      content?: { value?: Partial<LowScoreCriteriaConfigValue> };
      value?: Partial<LowScoreCriteriaConfigValue>;
    };

    if (response?.status === "failure" || response?.status === "unauthorized") {
      return rejectWithValue(
        response.message || "Failed to fetch Low Score criteria settings"
      );
    }

    const payload = response?.content ?? response;
    return normalizeLowScoreCriteria(
      extractConfigValue<LowScoreCriteriaConfigValue>(payload)
    );
  } catch (error: any) {
    return rejectWithValue(
      parseApiErrorMessage(error, "Failed to fetch Low Score criteria settings")
    );
  }
});

export const updateLowScoreCriteriaConfig = createAsyncThunk<
  LowScoreCriteriaConfigValue,
  UpdateLowScoreCriteriaPayload,
  { rejectValue: string }
>(
  "vendorAdmin/updateLowScoreCriteriaConfig",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const normalized = normalizeLowScoreCriteria(value);
      const response = (await post(API_URLS.upsertVendorConfig(), {
        key,
        value: normalized,
      })) as {
        status?: string;
        message?: string;
        content?: { value?: Partial<LowScoreCriteriaConfigValue> };
        value?: Partial<LowScoreCriteriaConfigValue>;
      };

      if (
        response?.status === "failure" ||
        response?.status === "unauthorized"
      ) {
        return rejectWithValue(
          response.message || "Failed to save Low Score criteria settings"
        );
      }

      const payload = response?.content ?? response;
      return normalizeLowScoreCriteria(
        extractConfigValue<LowScoreCriteriaConfigValue>(payload) ?? normalized
      );
    } catch (error: any) {
      return rejectWithValue(
        parseApiErrorMessage(
          error,
          "Failed to save Low Score criteria settings"
        )
      );
    }
  }
);

export const fetchPortfolioNeedsAttentionConfig = createAsyncThunk<
  PortfolioNeedsAttentionSettings,
  void,
  { rejectValue: string }
>(
  "vendorAdmin/fetchPortfolioNeedsAttentionConfig",
  async (_, { rejectWithValue }) => {
    try {
      const response = (await get(
        API_URLS.fetchPortfolioNeedsAttentionConfig()
      )) as {
        status?: string;
        message?: string;
        content?: { value?: Partial<PortfolioNeedsAttentionSettings> };
        value?: Partial<PortfolioNeedsAttentionSettings>;
      };

      if (
        response?.status === "failure" ||
        response?.status === "unauthorized"
      ) {
        return rejectWithValue(
          response.message || "Failed to fetch Needs Attention settings"
        );
      }

      const payload = response?.content ?? response;
      return normalizePortfolioNeedsAttentionValue(
        extractConfigValue<PortfolioNeedsAttentionSettings>(payload)
      );
    } catch (error: any) {
      return rejectWithValue(
        parseApiErrorMessage(error, "Failed to fetch Needs Attention settings")
      );
    }
  }
);

export const updatePortfolioNeedsAttentionConfig = createAsyncThunk<
  PortfolioNeedsAttentionSettings,
  UpdatePortfolioNeedsAttentionPayload,
  { rejectValue: string }
>(
  "vendorAdmin/updatePortfolioNeedsAttentionConfig",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const normalized = normalizePortfolioNeedsAttentionValue(value);
      const response = (await post(API_URLS.upsertVendorConfig(), {
        key,
        value: normalized,
      })) as {
        status?: string;
        message?: string;
        content?: { value?: Partial<PortfolioNeedsAttentionSettings> };
        value?: Partial<PortfolioNeedsAttentionSettings>;
      };

      if (
        response?.status === "failure" ||
        response?.status === "unauthorized"
      ) {
        return rejectWithValue(
          response.message || "Failed to save Needs Attention settings"
        );
      }

      const payload = response?.content ?? response;
      return normalizePortfolioNeedsAttentionValue(
        extractConfigValue<PortfolioNeedsAttentionSettings>(payload) ??
          normalized
      );
    } catch (error: any) {
      return rejectWithValue(
        parseApiErrorMessage(error, "Failed to save Needs Attention settings")
      );
    }
  }
);

export const updateAccountTeamRoleLimits = createAsyncThunk<
  AccountTeamRoleLimitsConfig,
  UpdateAccountTeamRoleLimitsPayload,
  { rejectValue: string }
>(
  "vendorAdmin/updateAccountTeamRoleLimits",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const response = (await post(API_URLS.upsertVendorConfig(), {
        key,
        value: normalizeAccountTeamRoleLimits(value),
      })) as {
        status?: string;
        message?: string;
        content?: AccountTeamRoleLimitsConfig;
      };

      if (
        response?.status === "failure" ||
        response?.status === "unauthorized"
      ) {
        return rejectWithValue(
          response.message || "Failed to save account team role limits"
        );
      }

      const payload = (response?.content ??
        response) as AccountTeamRoleLimitsConfig;
      return {
        id: payload?.id,
        key: payload?.key || key,
        value: normalizeAccountTeamRoleLimits(payload?.value ?? value),
        created_at: payload?.created_at,
        updated_at: payload?.updated_at,
      };
    } catch (error: any) {
      return rejectWithValue(
        parseApiErrorMessage(error, "Failed to save account team role limits")
      );
    }
  }
);

/** Raw vendor_config read for a settings section (Portfolio Settings
 * modal). Resolves with the untouched API response so the caller keeps its
 * own status/shape handling; rejects with the error's message. */
export const fetchVendorConfigRaw = createAsyncThunk<
  unknown,
  string,
  { rejectValue: string }
>("vendorAdmin/fetchVendorConfigRaw", async (url, { rejectWithValue }) => {
  try {
    return await get(url);
  } catch (error: unknown) {
    return rejectWithValue((error as Error)?.message || "");
  }
});

/** Raw vendor_config upsert (Portfolio Settings modal). Resolves with the
 * untouched API response; rejects with the error's message (often the JSON
 * error body). */
export const upsertVendorConfigRaw = createAsyncThunk<
  unknown,
  { key: string; value: unknown },
  { rejectValue: string }
>("vendorAdmin/upsertVendorConfigRaw", async (body, { rejectWithValue }) => {
  try {
    return await post(API_URLS.upsertVendorConfig(), body);
  } catch (error: unknown) {
    return rejectWithValue((error as Error)?.message || "");
  }
});

const vendorAdminSlice = createSlice({
  name: "vendorAdmin",
  initialState,
  reducers: {
    clearUpdateState: state => {
      state.updateLoading = false;
      state.updateError = null;
      state.updateSuccess = false;
    },
    clearDeleteUserState: state => {
      state.deleteUserLoading = false;
      state.deleteUserError = null;
      state.deleteUserSuccess = false;
    },
    clearResetPasswordState: state => {
      state.resetPasswordLoading = false;
      state.resetPasswordError = null;
      state.resetPasswordSuccess = false;
    },
    clearUpdateUserState: state => {
      state.updateUserLoading = false;
      state.updateUserError = null;
      state.updateUserSuccess = false;
    },
    clearCreateUserState: state => {
      state.createUserLoading = false;
      state.createUserError = null;
      state.createUserSuccess = false;
    },
    clearResendWelcomeEmailState: state => {
      state.resendWelcomeEmailLoading = false;
      state.resendWelcomeEmailError = null;
      state.resendWelcomeEmailSuccess = false;
    },
    clearColorCodingUpdateState: state => {
      state.colorCodingUpdateLoading = false;
      state.colorCodingUpdateError = null;
      state.colorCodingUpdateSuccess = false;
    },
    clearHealthScoreThresholdsUpdateState: state => {
      state.healthScoreThresholdsUpdateLoading = false;
      state.healthScoreThresholdsUpdateError = null;
      state.healthScoreThresholdsUpdateSuccess = false;
    },
    clearAssignmentRecommendationUpdateState: state => {
      state.assignmentRecommendationUpdateLoading = false;
      state.assignmentRecommendationUpdateError = null;
      state.assignmentRecommendationUpdateSuccess = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchVendorAdminStatistics.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendorAdminStatistics.fulfilled, (state, action) => {
        state.loading = false;
        state.statistics = action.payload;
      })
      .addCase(fetchVendorAdminStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchVendorAdminGrades.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendorAdminGrades.fulfilled, (state, action) => {
        state.loading = false;
        state.grades = action.payload;
      })
      .addCase(fetchVendorAdminGrades.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchVendorAdminAccountTiers.pending, state => {
        state.accountTiersLoading = true;
        state.error = null;
      })
      .addCase(fetchVendorAdminAccountTiers.fulfilled, (state, action) => {
        state.accountTiersLoading = false;
        state.accountTiers = action.payload;
      })
      .addCase(fetchVendorAdminAccountTiers.rejected, (state, action) => {
        state.accountTiersLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchVendorAdminUsers.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendorAdminUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.usersResponse = action.payload;
      })
      .addCase(fetchVendorAdminUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateGrade.pending, state => {
        state.updateLoading = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateGrade.fulfilled, state => {
        state.updateError = null;
        state.updateLoading = false;
        state.updateSuccess = true;
      })
      .addCase(updateGrade.rejected, (state, action) => {
        state.updateSuccess = false;
        state.updateLoading = false;
        state.updateError = action.payload as string;
      })
      .addCase(updateAccountTier.pending, state => {
        state.updateLoading = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateAccountTier.fulfilled, state => {
        state.updateError = null;
        state.updateLoading = false;
        state.updateSuccess = true;
      })
      .addCase(updateAccountTier.rejected, (state, action) => {
        state.updateSuccess = false;
        state.updateLoading = false;
        state.updateError = action.payload as string;
      })
      .addCase(deleteVendorUser.pending, state => {
        state.deleteUserLoading = true;
        state.deleteUserError = null;
        state.deleteUserSuccess = false;
      })
      .addCase(deleteVendorUser.fulfilled, state => {
        state.deleteUserLoading = false;
        state.deleteUserSuccess = true;
      })
      .addCase(deleteVendorUser.rejected, (state, action) => {
        state.deleteUserLoading = false;
        state.deleteUserError = action.payload as string;
      })
      .addCase(resetVendorUserPassword.pending, state => {
        state.resetPasswordLoading = true;
        state.resetPasswordError = null;
        state.resetPasswordSuccess = false;
      })
      .addCase(resetVendorUserPassword.fulfilled, state => {
        state.resetPasswordLoading = false;
        state.resetPasswordSuccess = true;
        toast.success(
          "Password reset link has been sent to your email address."
        );
      })
      .addCase(resetVendorUserPassword.rejected, (state, action) => {
        state.resetPasswordLoading = false;
        state.resetPasswordError = action.payload as string;
        toast.error(
          (action.payload as string) ||
            "Failed to update password. Please try again."
        );
      })
      .addCase(updateVendorUser.pending, state => {
        state.updateUserLoading = true;
        state.updateUserError = null;
        state.updateUserSuccess = false;
      })
      .addCase(updateVendorUser.fulfilled, state => {
        state.updateUserLoading = false;
        state.updateUserSuccess = true;
      })
      .addCase(updateVendorUser.rejected, (state, action) => {
        state.updateUserLoading = false;
        state.updateUserError = action.payload as string;
        // Error toast owned by VendorUserManagementContainer (SuccessNotification)
      })
      .addCase(createVendorUser.pending, state => {
        state.createUserLoading = true;
        state.createUserError = null;
        state.createUserSuccess = false;
      })
      .addCase(createVendorUser.fulfilled, state => {
        state.createUserLoading = false;
        state.createUserSuccess = true;
      })
      .addCase(createVendorUser.rejected, (state, action) => {
        state.createUserLoading = false;
        state.createUserError = action.payload as string;
      })
      .addCase(resendWelcomeEmail.pending, state => {
        state.resendWelcomeEmailLoading = true;
        state.resendWelcomeEmailError = null;
        state.resendWelcomeEmailSuccess = false;
      })
      .addCase(resendWelcomeEmail.fulfilled, state => {
        state.resendWelcomeEmailLoading = false;
        state.resendWelcomeEmailSuccess = true;
      })
      .addCase(resendWelcomeEmail.rejected, (state, action) => {
        state.resendWelcomeEmailLoading = false;
        state.resendWelcomeEmailError = action.payload as string;
        state.resendWelcomeEmailSuccess = false;
      })
      .addCase(fetchColorCodingThresholds.pending, state => {
        state.colorCodingLoading = true;
        state.colorCodingError = null;
      })
      .addCase(fetchColorCodingThresholds.fulfilled, (state, action) => {
        state.colorCodingLoading = false;
        state.colorCodingThresholds = action.payload;
        state.colorCodingThresholdId = action.payload?.id || null;
      })
      .addCase(fetchColorCodingThresholds.rejected, (state, action) => {
        state.colorCodingLoading = false;
        state.colorCodingError = action.payload as string;
        // Set defaults on error
        state.colorCodingThresholds = {
          id: null,
          utilization_percentage: {
            greenThreshold: 100.0,
            yellowThreshold: 120.0,
          },
        };
        state.colorCodingThresholdId = null;
      })
      .addCase(updateColorCodingThresholds.pending, state => {
        state.colorCodingUpdateLoading = true;
        state.colorCodingUpdateError = null;
        state.colorCodingUpdateSuccess = false;
      })
      .addCase(updateColorCodingThresholds.fulfilled, state => {
        state.colorCodingUpdateLoading = false;
        state.colorCodingUpdateSuccess = true;
        toast.success(
          "Color coding thresholds have been updated successfully."
        );
      })
      .addCase(updateColorCodingThresholds.rejected, (state, action) => {
        state.colorCodingUpdateLoading = false;
        state.colorCodingUpdateError = action.payload as string;
        state.colorCodingUpdateSuccess = false;
        toast.error(
          (action.payload as string) ||
            "Failed to save color coding thresholds. Please try again."
        );
      })
      .addCase(fetchAssignmentRecommendationSettings.pending, state => {
        state.assignmentRecommendationSettingsLoading = true;
        state.assignmentRecommendationSettingsError = null;
      })
      .addCase(
        fetchAssignmentRecommendationSettings.fulfilled,
        (state, action) => {
          state.assignmentRecommendationSettingsLoading = false;
          state.assignmentRecommendationSettings = action.payload;
        }
      )
      .addCase(
        fetchAssignmentRecommendationSettings.rejected,
        (state, action) => {
          state.assignmentRecommendationSettingsLoading = false;
          state.assignmentRecommendationSettingsError =
            action.payload as string;
        }
      )
      .addCase(updateAssignmentRecommendationSettings.pending, state => {
        state.assignmentRecommendationUpdateLoading = true;
        state.assignmentRecommendationUpdateError = null;
        state.assignmentRecommendationUpdateSuccess = false;
      })
      .addCase(updateAssignmentRecommendationSettings.fulfilled, state => {
        state.assignmentRecommendationUpdateLoading = false;
        state.assignmentRecommendationUpdateSuccess = true;
      })
      .addCase(
        updateAssignmentRecommendationSettings.rejected,
        (state, action) => {
          state.assignmentRecommendationUpdateLoading = false;
          state.assignmentRecommendationUpdateError = action.payload as string;
          state.assignmentRecommendationUpdateSuccess = false;
        }
      )
      .addCase(fetchAssignmentRecommendationHistory.pending, state => {
        state.assignmentRecommendationHistoryLoading = true;
        state.assignmentRecommendationHistoryError = null;
      })
      .addCase(
        fetchAssignmentRecommendationHistory.fulfilled,
        (state, action) => {
          state.assignmentRecommendationHistoryLoading = false;
          state.assignmentRecommendationHistory = action.payload || [];
        }
      )
      .addCase(
        fetchAssignmentRecommendationHistory.rejected,
        (state, action) => {
          state.assignmentRecommendationHistoryLoading = false;
          state.assignmentRecommendationHistoryError = action.payload as string;
        }
      )
      .addCase(fetchAccountTeamRoleLimits.pending, state => {
        state.accountTeamRoleLimitsLoading = true;
        state.accountTeamRoleLimitsError = null;
      })
      .addCase(fetchAccountTeamRoleLimits.fulfilled, (state, action) => {
        state.accountTeamRoleLimitsLoading = false;
        state.accountTeamRoleLimits = action.payload.value;
        state.accountTeamRoleLimitsConfigId = action.payload.id ?? null;
        state.accountTeamRoleLimitsConfigKey = action.payload.key;
      })
      .addCase(fetchAccountTeamRoleLimits.rejected, (state, action) => {
        state.accountTeamRoleLimitsLoading = false;
        state.accountTeamRoleLimitsError = action.payload as string;
      })
      .addCase(updateAccountTeamRoleLimits.pending, state => {
        state.accountTeamRoleLimitsUpdateLoading = true;
        state.accountTeamRoleLimitsUpdateError = null;
      })
      .addCase(updateAccountTeamRoleLimits.fulfilled, (state, action) => {
        state.accountTeamRoleLimitsUpdateLoading = false;
        state.accountTeamRoleLimits = action.payload.value;
        state.accountTeamRoleLimitsConfigId = action.payload.id ?? null;
        state.accountTeamRoleLimitsConfigKey = action.payload.key;
      })
      .addCase(updateAccountTeamRoleLimits.rejected, (state, action) => {
        state.accountTeamRoleLimitsUpdateLoading = false;
        state.accountTeamRoleLimitsUpdateError = action.payload as string;
      })
      .addCase(fetchDisconnectFlowConfig.pending, state => {
        state.disconnectFlowSettingsLoading = true;
        state.disconnectFlowSettingsError = null;
      })
      .addCase(fetchDisconnectFlowConfig.fulfilled, (state, action) => {
        state.disconnectFlowSettingsLoading = false;
        state.disconnectFlowValue = action.payload.value;
        state.disconnectFlowConfigId = action.payload.id ?? null;
        state.disconnectFlowConfigKey = action.payload.key;
      })
      .addCase(fetchDisconnectFlowConfig.rejected, (state, action) => {
        state.disconnectFlowSettingsLoading = false;
        state.disconnectFlowSettingsError = action.payload as string;
      })
      .addCase(updateDisconnectFlowConfig.pending, state => {
        state.disconnectFlowSettingsUpdateLoading = true;
        state.disconnectFlowSettingsUpdateError = null;
      })
      .addCase(updateDisconnectFlowConfig.fulfilled, (state, action) => {
        state.disconnectFlowSettingsUpdateLoading = false;
        state.disconnectFlowValue = action.payload.value;
        state.disconnectFlowConfigId = action.payload.id ?? null;
        state.disconnectFlowConfigKey = action.payload.key;
      })
      .addCase(updateDisconnectFlowConfig.rejected, (state, action) => {
        state.disconnectFlowSettingsUpdateLoading = false;
        state.disconnectFlowSettingsUpdateError = action.payload as string;
      })
      .addCase(fetchOrganizationSettings.pending, state => {
        state.organizationSettingsLoading = true;
      })
      .addCase(fetchOrganizationSettings.fulfilled, (state, action) => {
        state.organizationSettingsLoading = false;
        state.organizationSettingsFetched = true;
        state.organizationSettingsError = null;
        state.organizationSettings = action.payload;
      })
      .addCase(fetchOrganizationSettings.rejected, (state, action) => {
        state.organizationSettingsLoading = false;
        state.organizationSettingsFetched = true;
        state.organizationSettingsError = action.payload as string;
      })
      .addCase(fetchPortfolioMapsConfig.pending, state => {
        state.portfolioMapsConfigLoading = true;
      })
      .addCase(fetchPortfolioMapsConfig.fulfilled, (state, action) => {
        state.portfolioMapsConfigLoading = false;
        state.portfolioMapsConfigFetched = true;
        state.portfolioMapsConfigError = null;
        state.portfolioMapsConfig = action.payload;
      })
      .addCase(fetchPortfolioMapsConfig.rejected, (state, action) => {
        state.portfolioMapsConfigLoading = false;
        state.portfolioMapsConfigFetched = true;
        state.portfolioMapsConfigError = action.payload as string;
      })
      .addCase(fetchRlSettingConfig.pending, state => {
        state.rlSettingLoading = true;
        state.rlSettingError = null;
      })
      .addCase(fetchRlSettingConfig.fulfilled, (state, action) => {
        state.rlSettingLoading = false;
        state.rlSettingValue = normalizeRlSettingValue(
          action.payload.value as Record<string, unknown>
        );
        state.rlSettingConfigId = action.payload.id ?? null;
        state.rlSettingConfigKey = action.payload.key;
      })
      .addCase(fetchRlSettingConfig.rejected, (state, action) => {
        state.rlSettingLoading = false;
        state.rlSettingError = action.payload as string;
      })
      .addCase(updateRlSettingConfig.pending, state => {
        state.rlSettingUpdateLoading = true;
        state.rlSettingUpdateError = null;
      })
      .addCase(updateRlSettingConfig.fulfilled, (state, action) => {
        state.rlSettingUpdateLoading = false;
        state.rlSettingValue = normalizeRlSettingValue(
          action.payload.value as Record<string, unknown>
        );
        state.rlSettingConfigId = action.payload.id ?? null;
        state.rlSettingConfigKey = action.payload.key;
      })
      .addCase(updateRlSettingConfig.rejected, (state, action) => {
        state.rlSettingUpdateLoading = false;
        state.rlSettingUpdateError = action.payload as string;
      })
      .addCase(fetchWeeklyDigestConfig.pending, state => {
        state.weeklyDigestLoading = true;
        state.weeklyDigestError = null;
      })
      .addCase(fetchWeeklyDigestConfig.fulfilled, (state, action) => {
        state.weeklyDigestLoading = false;
        state.weeklyDigestValue = normalizeWeeklyDigestValue(
          action.payload.value as Record<string, unknown>
        );
      })
      .addCase(fetchWeeklyDigestConfig.rejected, (state, action) => {
        state.weeklyDigestLoading = false;
        state.weeklyDigestError = action.payload as string;
      })
      .addCase(updateWeeklyDigestConfig.pending, state => {
        state.weeklyDigestUpdateLoading = true;
        state.weeklyDigestUpdateError = null;
      })
      .addCase(updateWeeklyDigestConfig.fulfilled, (state, action) => {
        state.weeklyDigestUpdateLoading = false;
        state.weeklyDigestValue = normalizeWeeklyDigestValue(
          action.payload.value as Record<string, unknown>
        );
        if (state.weeklyDigestStatus) {
          state.weeklyDigestStatus = {
            ...state.weeklyDigestStatus,
            org_active: Boolean(state.weeklyDigestValue?.enabled),
            sending_enabled:
              state.weeklyDigestStatus.org_entitled &&
              Boolean(state.weeklyDigestValue?.enabled),
          };
        }
      })
      .addCase(updateWeeklyDigestConfig.rejected, (state, action) => {
        state.weeklyDigestUpdateLoading = false;
        state.weeklyDigestUpdateError = action.payload as string;
      })
      .addCase(fetchWeeklyDigestStatus.pending, state => {
        state.weeklyDigestStatusLoading = true;
      })
      .addCase(fetchWeeklyDigestStatus.fulfilled, (state, action) => {
        state.weeklyDigestStatusLoading = false;
        state.weeklyDigestStatus = action.payload;
      })
      .addCase(fetchWeeklyDigestStatus.rejected, state => {
        state.weeklyDigestStatusLoading = false;
      })
      .addCase(fetchHealthScoreThresholds.pending, state => {
        state.healthScoreThresholdsLoading = true;
        state.healthScoreThresholdsError = null;
      })
      .addCase(fetchHealthScoreThresholds.fulfilled, (state, action) => {
        state.healthScoreThresholdsLoading = false;
        state.healthScoreThresholdsFetched = true;
        state.healthScoreThresholds = action.payload;
      })
      .addCase(fetchHealthScoreThresholds.rejected, (state, action) => {
        state.healthScoreThresholdsLoading = false;
        state.healthScoreThresholdsFetched = true;
        state.healthScoreThresholdsError = action.payload as string;
        state.healthScoreThresholds = DEFAULT_HEALTH_SCORE_THRESHOLDS;
      })
      .addCase(updateHealthScoreThresholds.pending, state => {
        state.healthScoreThresholdsUpdateLoading = true;
        state.healthScoreThresholdsUpdateError = null;
        state.healthScoreThresholdsUpdateSuccess = false;
      })
      .addCase(updateHealthScoreThresholds.fulfilled, (state, action) => {
        state.healthScoreThresholdsUpdateLoading = false;
        state.healthScoreThresholdsUpdateSuccess = true;
        state.healthScoreThresholdsFetched = true;
        state.healthScoreThresholds = action.payload;
      })
      .addCase(updateHealthScoreThresholds.rejected, (state, action) => {
        state.healthScoreThresholdsUpdateLoading = false;
        state.healthScoreThresholdsUpdateSuccess = false;
        state.healthScoreThresholdsUpdateError = action.payload as string;
      })
      .addCase(fetchLowScoreCriteriaConfig.pending, state => {
        state.lowScoreCriteriaLoading = true;
        state.lowScoreCriteriaError = null;
      })
      .addCase(fetchLowScoreCriteriaConfig.fulfilled, (state, action) => {
        state.lowScoreCriteriaLoading = false;
        state.lowScoreCriteriaValue = action.payload;
      })
      .addCase(fetchLowScoreCriteriaConfig.rejected, (state, action) => {
        state.lowScoreCriteriaLoading = false;
        state.lowScoreCriteriaValue = DEFAULT_LOW_SCORE_CRITERIA;
        state.lowScoreCriteriaError = action.payload as string;
      })
      .addCase(updateLowScoreCriteriaConfig.pending, state => {
        state.lowScoreCriteriaUpdateLoading = true;
        state.lowScoreCriteriaUpdateError = null;
      })
      .addCase(updateLowScoreCriteriaConfig.fulfilled, (state, action) => {
        state.lowScoreCriteriaUpdateLoading = false;
        state.lowScoreCriteriaValue = action.payload;
      })
      .addCase(updateLowScoreCriteriaConfig.rejected, (state, action) => {
        state.lowScoreCriteriaUpdateLoading = false;
        state.lowScoreCriteriaUpdateError = action.payload as string;
      })
      .addCase(fetchPortfolioNeedsAttentionConfig.pending, state => {
        state.portfolioNeedsAttentionLoading = true;
        state.portfolioNeedsAttentionError = null;
      })
      .addCase(
        fetchPortfolioNeedsAttentionConfig.fulfilled,
        (state, action) => {
          state.portfolioNeedsAttentionLoading = false;
          state.portfolioNeedsAttentionValue = action.payload;
        }
      )
      .addCase(fetchPortfolioNeedsAttentionConfig.rejected, (state, action) => {
        state.portfolioNeedsAttentionLoading = false;
        state.portfolioNeedsAttentionValue = DEFAULT_PORTFOLIO_NEEDS_ATTENTION;
        state.portfolioNeedsAttentionError = action.payload as string;
      })
      .addCase(updatePortfolioNeedsAttentionConfig.pending, state => {
        state.portfolioNeedsAttentionUpdateLoading = true;
        state.portfolioNeedsAttentionUpdateError = null;
      })
      .addCase(
        updatePortfolioNeedsAttentionConfig.fulfilled,
        (state, action) => {
          state.portfolioNeedsAttentionUpdateLoading = false;
          state.portfolioNeedsAttentionValue = action.payload;
        }
      )
      .addCase(
        updatePortfolioNeedsAttentionConfig.rejected,
        (state, action) => {
          state.portfolioNeedsAttentionUpdateLoading = false;
          state.portfolioNeedsAttentionUpdateError = action.payload as string;
        }
      );
  },
});

export const {
  clearUpdateState,
  clearDeleteUserState,
  clearResetPasswordState,
  clearUpdateUserState,
  clearCreateUserState,
  clearResendWelcomeEmailState,
  clearColorCodingUpdateState,
  clearAssignmentRecommendationUpdateState,
  clearHealthScoreThresholdsUpdateState,
} = vendorAdminSlice.actions;
export default vendorAdminSlice.reducer;
